import { useMutation } from '@tanstack/react-query';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { ValidationMessage, ValidationSectionKey } from '@/constants';
import type { RequiredFormField } from '@/contexts/types';
import { useValidation } from '@/contexts/validationContext';
import { postData } from '@/utils/postData';

interface NotifyProps {
  isError: boolean;
  message: string;
}

interface SendOrderResponse {
  isSuccess: boolean;
  data?: {
    redirectUrl?: string;
    errorMessage?: string | null;
    threeDSecureAuthenticationRequired?: boolean;
    threeDSecureRedirectUrl?: string;
  };
  messages?: string[];
}

interface UseSendOrderOptions {
  onThreeDSecureRequired?: (url: string) => void;
  /**
   * Fired when the order request settles, whatever the outcome. Callers holding a
   * submit guard across the request use this to release it, so a decline or a
   * validation failure does not lock the customer out of paying (#2940).
   */
  onSettled?: () => void;
}

const notify = ({ message, isError }: NotifyProps) => {
  
  const persistentTimeout = isError ? 10000000 : 5000; 
  console.log('Setting notification with timeout = ' + persistentTimeout);
  NotificationsQueue.add(
    {
      notification: (
        <NotificationMessage $isError={isError}>{message}</NotificationMessage>
      ),
    },
    { timeout: persistentTimeout },
  );
};

export const useSendOrder = (
  endpoint: string,
  options?: UseSendOrderOptions,
) => {
  const { onSettled, onThreeDSecureRequired } = options || {};
  const { setAddressFieldError, setValidationError } = useValidation();

  const { isPending, mutate } = useMutation<
    SendOrderResponse,
    Error,
    Record<string, unknown>
  >({
    mutationFn: postData(endpoint),
    onError: error => {
      console.error(`Failed to submit payment: ${error.message}`);
      //notify({ isError: true, message: 'Failed to submit payment.' });
    },
    onSuccess: data => {
      console.log('Order response received', data);

      // Handle 3D Secure
      const threeDSRequired = data.data?.threeDSecureAuthenticationRequired;
      const threeDSUrl = data.data?.threeDSecureRedirectUrl;

      if (threeDSRequired && threeDSUrl) {
        console.log('3D Secure authentication required');
        if (onThreeDSecureRequired) {
          onThreeDSecureRequired(threeDSUrl);
        } else {
          // Fallback to redirect if no handler provided
          window.location.href = threeDSUrl;
        }
      } else if (data.data?.errorMessage) {
        // Handle error message in data
        notify({ isError: true, message: data.data.errorMessage });
      } else if (data.isSuccess && data.data?.redirectUrl) {
        // Handle successful order with redirect URL
        console.log(
          'Order created successfully, redirecting to:',
          data.data.redirectUrl,
        );
        window.location.href = data.data.redirectUrl;
      } else if (data.isSuccess) {
        // Handle successful order without redirect URL
        console.log('Payment successful, but no redirect URL provided.');
        notify({ isError: false, message: 'Payment successful!' });
      } else if (!data.isSuccess && (data.messages?.length ?? 0) > 0) {
        // Handle generic failure
        console.info('Payment failed, display errors.');
        notify({ isError: true, message: data.messages?.[0] ?? '' });

        const messages =
          data.messages?.map(message => message.split('|')[1]) ?? [];

        for (const message of messages) {
          if (Object.keys(ValidationSectionKey).includes(message[0])) {
            if (message.length === 3) {
              const addressType = message[0].startsWith('Billing')
                ? 'billing'
                : 'delivery';
              const formField = (message[1].substring(0, 1).toLowerCase() +
                message[1].substring(1)) as RequiredFormField;

              setValidationError(
                ValidationSectionKey[
                  message[0] as keyof typeof ValidationSectionKey
                ],
                ValidationMessage.Address.replace(
                  '${addressType}',
                  addressType,
                ),
              );
              setAddressFieldError(addressType, formField, message[2]);
            } else {
              setValidationError(
                ValidationSectionKey[
                  message[0] as keyof typeof ValidationSectionKey
                ],
                message[1],
              );
            }
          }
        }
      } else {
        // Unexpected response
        console.error('Unexpected response format', data);
        notify({
          isError: true,
          message: 'An unexpected error occurred. Please try again.',
        });
      }
    },
    onSettled: () => {
      onSettled?.();
    },
  });

  return { isPending, sendOrder: mutate };
};
